import { FC, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { warmUpMediaCors } from '../../../audio-preview/core/audioAnalyser';
import { analyseAudioPrefix } from '../../../audio-preview/core/offlineSpectrum';
import { analyzePeaks } from '../../../audio-preview/core/peaksMotion';
import {
  getSpectrumCategory,
  subscribeSpectrumCategory,
} from '../../../audio-preview/core/spectrumCategory';
import { SpectrumFrame } from '../../../audio-preview/core/spectrumFeatures';
import { usePeaks } from '../../../audio-preview/core/usePeaks';
import AudioThumbnailCanvas, {
  AudioThumbnailGlowTrigger,
} from './AudioThumbnailCanvas';
import { audioThumbnailSeed } from './audioThumbnailPalette';

/**
 * ジャケット画像を持たないオーディオの生成サムネイル（データ取得つき）。
 *
 * 一覧のカード（AudioPreview）とフローティングプレイヤーバーの両方から使うため、
 * 「音源を分類して canvas に渡す」までをここに閉じている。
 * 同じ音源なら両者で同じ絵になる必要があるので、配色の種は audioThumbnailSeed に
 * 一元化し、呼び出し側で組み立てさせない。
 *
 * カテゴリは実測（spectrumCategory）のみで決め、判定が付くまでは neutral
 * （種ごとに違う淡い色）で出す。peaks は色ではなく動きにだけ使う。
 */

export interface GenerativeAudioThumbnailProps {
  /** 音源 URL。配色の種と peaks キャッシュのキーになる */
  src: string;
  /** 波形 JSON の URL。無いと動きの駆動信号が得られず自律ドリフトのみになる */
  waveformUrl?: string | null;
  /** peaks キャッシュのキー。波形プレイヤーと共有できる ID があれば渡す */
  cacheKey?: string | null;
  isPlaying: boolean;
  isHovering?: boolean;
  glowTrigger?: AudioThumbnailGlowTrigger;
  /** 再生位置（秒）を返す。毎フレーム呼ばれる */
  getCurrentTime: () => number;
  /**
   * 再生中トラックの実スペクトル。渡されて中身が取れる間は擬似帯域より優先する。
   * 省略時・null 時は peaks 由来の駆動にフォールバックする。
   */
  readSpectrum?: () => SpectrumFrame | null;
  /**
   * peaks の取得を「実際に動かす時」まで遅らせるか。
   * 一覧を開いた瞬間に全カードが波形 JSON を取りに行くのを避けるために使う。
   * peaks は動きの駆動にしか使わないので、色には影響しない。
   */
  deferPeaks?: boolean;
}

const GenerativeAudioThumbnail: FC<GenerativeAudioThumbnailProps> = ({
  src,
  waveformUrl = null,
  cacheKey = null,
  isPlaying,
  isHovering = false,
  glowTrigger,
  getCurrentTime,
  readSpectrum,
  deferPeaks = false,
}) => {
  // 一度でも動かす条件が揃ったら取得を許可し、以降は取り下げない
  // （再生を止めた瞬間に peaks を捨てて絵が変わってしまうのを防ぐ）
  const [motionRequested, setMotionRequested] = useState(false);
  useEffect(() => {
    if (isHovering || isPlaying) setMotionRequested(true);
  }, [isHovering, isPlaying]);

  // 再生時の解析タップは CORS が使えると判っている音源にしか張れない。判定には
  // 実測が要るので、サムネイルが並んだこの時点でオリジン単位に一度だけ測っておく。
  useEffect(() => {
    warmUpMediaCors(src);
  }, [src]);

  // 実測由来のカテゴリ。オフライン解析か再生中の解析のどちらかが書き込む
  const category = useSyncExternalStore(subscribeSpectrumCategory, () =>
    getSpectrumCategory(src),
  );

  // 分類は画面に入ってから。一覧の全カードが一斉に音源を取りに行かないよう、
  // 取得と解析は offlineSpectrum 側で同時実行数を絞ってある
  const analyseOnVisible = useCallback(() => {
    analyseAudioPrefix(src);
  }, [src]);

  // fallbackSrc を渡さないのは意図的。波形 JSON が無いファイルのために
  // 一覧の全カードが元メディアを一斉ダウンロード＆デコードするのを避ける。
  const { peaks } = usePeaks({
    cacheKey: cacheKey ?? waveformUrl ?? src,
    waveformUrl,
    enabled: Boolean(waveformUrl) && (!deferPeaks || motionRequested),
  });

  const motion = useMemo(() => (peaks ? analyzePeaks(peaks) : null), [peaks]);

  return (
    <AudioThumbnailCanvas
      category={category ?? 'neutral'}
      seed={audioThumbnailSeed(src)}
      motion={motion}
      isPlaying={isPlaying}
      isHovering={isHovering}
      glowTrigger={glowTrigger}
      getCurrentTime={getCurrentTime}
      readSpectrum={readSpectrum}
      onFirstVisible={analyseOnVisible}
    />
  );
};

export default GenerativeAudioThumbnail;
